import { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ActionDetails } from './ActionDetails';
import { ACTION_LABELS, StatusBadge, formatWhen } from './dutiShared';
import { Bot, Check, X, Loader2 } from 'lucide-react';

export const ActionCard = ({ action, onDone, decidable }) => {
  const [note, setNote] = useState('');
  const [payment, setPayment] = useState('cash');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(null);
  const id = action.action_id;
  const needsPrice = action.action_type === 'draft_product' && !(Number(action.payload?.product?.unit_price) > 0);

  const decide = async (kind) => {
    setBusy(kind);
    try {
      await api.post(`/duti/actions/${id}/${kind}`, { note, payment_method: payment, unit_price: price ? Number(price) : null });
      toast.success(kind === 'approve' ? 'Approved and applied' : 'Rejected');
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete that');
    } finally {
      setBusy(null);
    }
  };

  return (
    <article className="rounded-xl border bg-card p-5 space-y-4 transition-shadow hover:shadow-md" data-testid={`duti-action-card-${id}`}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{ACTION_LABELS[action.action_type]}</p>
          <h3 className="font-semibold leading-snug mt-0.5" data-testid={`duti-action-summary-${id}`}>{action.summary}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Asked by {action.requested_by_name || 'staff'} · {formatWhen(action.created_at)}
          </p>
        </div>
        <StatusBadge status={action.status} testId={`duti-action-status-${id}`} />
      </header>
      <div className="flex gap-2 rounded-lg bg-primary/5 p-3 text-sm">
        <Bot className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
        <p data-testid={`duti-action-rationale-${id}`}>{action.rationale}</p>
      </div>
      <ActionDetails action={action} />
      {action.decided_by_name && (
        <p className="text-xs text-muted-foreground">
          {action.status} by {action.decided_by_name} · {formatWhen(action.decided_at)}{action.decision_note ? ` — “${action.decision_note}”` : ''}
        </p>
      )}
      {decidable && action.status === 'pending' && (
        <footer className="space-y-3 border-t pt-4">
          {action.action_type === 'pos_pending_sale' && (
            <Select value={payment} onValueChange={setPayment}>
              <SelectTrigger className="w-48" data-testid={`duti-payment-select-${id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="mobile_money">Mobile money</SelectItem>
                <SelectItem value="card">Card</SelectItem>
              </SelectContent>
            </Select>
          )}
          {needsPrice && (
            <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
              placeholder="Selling price (GHS) — required" className="w-64" data-testid={`duti-price-input-${id}`} />
          )}
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note" rows={2} data-testid={`duti-note-input-${id}`} />
          <div className="flex gap-2">
            <Button onClick={() => decide('approve')} disabled={!!busy} data-testid={`duti-approve-btn-${id}`}>
              {busy === 'approve' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              {action.action_type === 'pos_pending_sale' ? 'Approve & take payment' : 'Approve'}
            </Button>
            <Button variant="outline" onClick={() => decide('reject')} disabled={!!busy} data-testid={`duti-reject-btn-${id}`}>
              {busy === 'reject' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <X className="h-4 w-4 mr-1" />}Reject
            </Button>
          </div>
        </footer>
      )}
    </article>
  );
};
